import { useState } from 'react';
import { useField } from 'formik';

import { KubesoloOfflineBundle } from '@api/types.gen';

import { Badge } from '@@/Badge';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { FileUploadField } from '@@/form-components/FileUpload';
import { SwitchField } from '@@/form-components/SwitchField';
import { ExternalLink } from '@@/ExternalLink';

import { formatSize } from './formatSize';
import { useDeleteOfflineBundleMutation } from './queries/useDeleteOfflineBundleMutation';
import { useKubesoloVersions } from './queries/useKubesoloVersions';
import { useOfflineBundles } from './queries/useOfflineBundles';
import { useUploadOfflineBundleMutation } from './queries/useUploadOfflineBundleMutation';

const RELEASES_URL = 'https://github.com/portainer/kubesolo/releases/tag/';

export function OfflineBundlesField() {
  const [field, , helpers] = useField<boolean>('offline');

  return (
    <>
      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            name={field.name}
            checked={field.value}
            onChange={(checked) => helpers.setValue(checked)}
            label="Machines can't reach the internet"
            labelClass="col-sm-3 col-lg-2"
            tooltip="The machines install KubeSolo from an offline bundle uploaded to Portainer, instead of downloading it from GitHub."
            data-cy="kubesolo-offline-switch"
          />
        </div>
      </div>

      {field.value && <OfflineBundles />}
    </>
  );
}

function OfflineBundles() {
  const bundled = useKubesoloVersions().data?.bundled;
  const bundlesQuery = useOfflineBundles();

  // the instructions name the bundled release, so wait for it
  if (!bundled) {
    return null;
  }

  const tarballPattern = `kubesolo-${bundled}-linux-<arch>[-musl]-offline.tar.gz`;

  return (
    <div className="form-group">
      <div className="col-sm-12 flex flex-col gap-2">
        <span className="small text-muted">
          Upload the offline tarball of KubeSolo {bundled} ({tarballPattern})
          for each architecture the machines have, from the{' '}
          <ExternalLink
            to={RELEASES_URL + bundled}
            data-cy="kubesolo-offline-release-link"
          >
            KubeSolo {bundled} release
          </ExternalLink>
          . Portainer checks each one against the release&apos;s SHA256SUMS.
        </span>

        <ul aria-label={`Offline bundles for ${bundled}`} className="m-0 p-0">
          {bundlesQuery.data?.map((bundle) => (
            <BundleItem key={bundle.name} bundle={bundle} />
          ))}
        </ul>
        {bundlesQuery.data?.length === 0 && (
          <span className="small text-muted">No offline bundle uploaded.</span>
        )}

        <UploadBundleButton />
      </div>
    </div>
  );
}

function BundleItem({ bundle }: { bundle: KubesoloOfflineBundle }) {
  const deleteMutation = useDeleteOfflineBundleMutation();

  return (
    <li className="flex items-center gap-4">
      <code>{bundle.name}</code>
      <span className="small text-muted">{formatSize(bundle.size)}</span>
      <Badge type="success">Verified</Badge>
      <DeleteButton
        size="xsmall"
        isLoading={deleteMutation.isLoading}
        confirmMessage={`Remove ${bundle.name}? Machines that need it can't install until it's uploaded again.`}
        onConfirmed={() => deleteMutation.mutate(bundle.name)}
        data-cy={`kubesolo-offline-bundle-remove-${bundle.name}`}
      />
    </li>
  );
}

function UploadBundleButton() {
  const uploadMutation = useUploadOfflineBundleMutation();
  const [progress, setProgress] = useState(0);
  const isUploading = uploadMutation.isLoading;

  return (
    <FileUploadField
      inputId="kubesolo-offline-bundle"
      title={
        isUploading ? `Uploading... ${progress}%` : 'Upload offline bundle'
      }
      accept=".tar.gz,.tgz,application/gzip"
      onChange={handleChange}
      disabled={isUploading}
      state={isUploading ? 'uploading' : undefined}
      // the mutation keeps the last file after the upload ends, so its name only shows while uploading
      hideFilename={!isUploading}
      value={uploadMutation.variables?.file}
      data-cy="kubesolo-offline-bundle-upload"
    />
  );

  function handleChange(file: File) {
    setProgress(0);
    uploadMutation.mutate({ file, onProgress: setProgress });
  }
}
